import React, { useState, useRef, useEffect, useMemo } from "react";
import { Modal, Button, Spinner, Alert } from "react-bootstrap";
import ListingCalendarService from "../../services/listing-calendar-service";
import { issuerInstitutions } from "../../helpr/issuerInstitutions";

/**
 * ConfirmReserveModal
 * - Shows an error in a separate Modal when the API returns one (no browser alerts)
 * - Shows a Success modal after reservation succeeds, which auto-closes after 5s
 *
 * Props:
 *  - show: boolean
 *  - onHide: function
 *  - reservationData: object
 *  - onSuccess: function(result) optional
 */

const ErrorModal = ({ show, onHide, message }) => (
  <Modal
    show={show}
    onHide={onHide}
    centered
    aria-labelledby="error-modal-title"
    backdrop="static"
  >
    <Modal.Header closeButton>
      <Modal.Title id="error-modal-title">Reservation Error</Modal.Title>
    </Modal.Header>

    <Modal.Body>
      <Alert variant="danger"><p>{message}</p></Alert>
    </Modal.Body>

    <Modal.Footer>
      <Button variant="secondary" onClick={onHide}>
        Close
      </Button>
    </Modal.Footer>
  </Modal>
);

const ConfirmReserveModal = ({ show, onHide, reservationData, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // success modal state
  const [successVisible, setSuccessVisible] = useState(false);
  const [successResult, setSuccessResult] = useState(null);
  const successTimerRef = useRef(null);

  // keep a stable service instance across renders
  const listingCalendarServiceRef = useRef(null);
  if (!listingCalendarServiceRef.current) {
    listingCalendarServiceRef.current = new ListingCalendarService();
  }
  const listingCalendarService = listingCalendarServiceRef.current;

  // build issuer lookup once
  const issuers = useMemo(() => issuerInstitutions(), []);

  // clear error whenever the main modal is closed or reservationData changes
  useEffect(() => {
    if (!show) setError("");
  }, [show, reservationData]);

  // cleanup timer on unmount/when modal hide
  useEffect(() => {
    return () => {
      if (successTimerRef.current) {
        clearTimeout(successTimerRef.current);
        successTimerRef.current = null;
      }
    };
  }, []);

  const friendlyIssuerCode = useMemo(() => {
    if (!reservationData) return "";

    const code =
      reservationData.issuerCode ||
      reservationData.institutionCode ||
      reservationData.issuer ||
      "";

    if (!code) return "";

    const found = issuers.find(
      (i) => i.value === code || i.institutionCode === code || i.name === code
    );

    return found ? `${String(code)} (${found.name})` : String(code);
  }, [reservationData, issuers]);

  const getErrorMessage = (err) => {
    console.debug("Raw error object:", err);

    try {
      if (!err) return "";

      const response = err.response || err;
      const data = (response && response.data) || response;

      if (data) {
        if (typeof data === "string") {
          try {
            const parsed = JSON.parse(data);
            if (parsed && parsed.message) return parsed.message;
            if (typeof parsed === "object") return JSON.stringify(parsed);
            return String(parsed);
          } catch {
            return data;
          }
        }

        if (typeof data === "object") {
          if (data.message) return data.message;
          if (data.error) return data.error;
          if (data.errors) {
            if (Array.isArray(data.errors)) return data.errors.join(", ");
            return JSON.stringify(data.errors);
          }
          if (data.body) {
            try {
              const parsedBody =
                typeof data.body === "string" ? JSON.parse(data.body) : data.body;
              if (parsedBody && parsedBody.message) return parsedBody.message;
              return typeof parsedBody === "string"
                ? parsedBody
                : JSON.stringify(parsedBody);
            } catch {}
          }
          try {
            return JSON.stringify(data);
          } catch {
            return String(data);
          }
        }
      }

      if (err.data) {
        if (err.data.message) return err.data.message;
        return typeof err.data === "string" ? err.data : JSON.stringify(err.data);
      }

      if (err.message) return err.message;

      return String(err);
    } catch (ex) {
      console.error("getErrorMessage error:", ex);
      return "An unknown error occurred";
    }
  };

  const finalizeSuccess = () => {
    // clear timer
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }

    // First notify parent so it can clear state / close parent modals
    try {
      if (typeof onSuccess === "function") {
        onSuccess(successResult);
      } else {
        // fallback to simply hiding this modal if no onSuccess provided
        onHide && onHide();
      }
    } catch (e) {
      console.error("Error calling onSuccess/onHide:", e);
      onHide && onHide();
    }

    // reload the page (user requested behavior)
    try {
      window.location.reload();
    } catch (e) {
      console.error("Failed to reload the page:", e);
    }
  };

  const startAutoCloseTimer = () => {
    // clear existing if any
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
    }
    successTimerRef.current = setTimeout(() => {
      finalizeSuccess();
    }, 5000);
  };

  const handleConfirm = async () => {
    if (!reservationData) {
      setError("Missing reservation information.");
      return;
    }

    setLoading(true);
    setError("");
    console.debug("Reservation payload (original):", reservationData);

    try {
      // Build minimal payload:
      const payloadToSend = {
        email: reservationData.email,
        issuerCode: reservationData.issuerCode,
        primaryListingDate: reservationData.primaryListingDate,
        secondaryListingDate: reservationData.secondaryListingDate
      };
      console.debug("Reservation payload (sent):", payloadToSend);

      const result = await listingCalendarService.unAuthRequestReservation(payloadToSend);

      setSuccessResult(result);
      setSuccessVisible(true);
      setLoading(false);

      // start auto-close countdown
      startAutoCloseTimer();
    } catch (err) {
      console.error("Reservation error (caught in modal):", err);
      const pretty = getErrorMessage(err);
      setError(pretty || "Reservation failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        {!successVisible ? (
          <>
            <Modal.Header closeButton>
              <Modal.Title>Confirm Reservation</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p>Are you sure you want to reserve these dates?</p>

              <p>
                <strong>Primary Listing Date:</strong>{" "}
                {reservationData && reservationData.primaryListingDate}
              </p>
              {reservationData && reservationData.secondaryListingDate ? (
                <p>
                  <strong>Secondary Listing Date:</strong>{" "}
                  {reservationData.secondaryListingDate}
                </p>
              ) : null}
              <p>
                <strong>Email:</strong> {reservationData && reservationData.email}
              </p>
              <p>
                <strong>Issuer Code:</strong> {friendlyIssuerCode}
              </p>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="secondary" onClick={onHide} disabled={loading}>
                Cancel
              </Button>

              <Button variant="primary" onClick={handleConfirm} disabled={loading}>
                {loading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />
                    {"  "}
                    Confirming...
                  </>
                ) : (
                  "Confirm"
                )}
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <>
            <Modal.Header closeButton onHide={finalizeSuccess}>
              <Modal.Title>Reservation Successful</Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <p>Your reservation was created successfully.</p>
              {successResult && successResult.data && successResult.data.reservation_reference ? (
                <p>
                  <strong>Reference:</strong> {String(successResult.data.reservation_reference)}
                </p>
              ) : null}
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
                <div style={{ marginLeft: 10 }}>
                  This dialog will close in 5 seconds and the page will reload.
                </div>
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button variant="primary" onClick={finalizeSuccess}>
                Close now
              </Button>
            </Modal.Footer>
          </>
        )}
      </Modal>

      <ErrorModal show={!!error} onHide={() => setError("")} message={error} />
    </>
  );
};

export default ConfirmReserveModal;
